import { DYNAMIC_SLOT_TAG } from "@hightouch/lib/cep/types/dynamic-slot-markers";
import type { Context, Liquid } from "@hightouch/lib/templating/liquid";

/**
 * Render-context key holding the prefetched, resolved slots, keyed by usage id.
 * The tag reads it without DB access, so render stays sync-safe.
 */
export const DYNAMIC_SLOTS_CONTEXT_KEY = "__dynamicSlots";

/** A slot resolved for render, keyed off a single `condition_case` column shared across the slot's usages. */
export type ResolvedDynamicSlot = {
  /** The slot library the column and branches belong to. */
  libraryId: string;
  /** The per-slot `condition_case` column on the profile row. */
  column: string;
  /** Branch id → this placement's content for that branch. */
  contentByBranchId: Record<string, string>;
  /** Rendered when the column is NULL or names no known branch. */
  fallbackContent: string;
  /** The branch `fallbackContent` comes from; null when the slot has none. */
  fallbackBranchId: string | null;
};

/**
 * The branch a recipient's column value selects: the named branch when the
 * slot carries it, else the fallback (whose id is null when the slot has none,
 * so the marker renders nothing). The render tag and the per-send record both
 * go through here so the warehouse never names a branch the email did not show.
 */
export function servedBranch(
  slot: Omit<ResolvedDynamicSlot, "libraryId" | "column">,
  value: unknown,
): { branchId: string | null; content: string } {
  const branchId = String(value);
  const content = slot.contentByBranchId[branchId];
  if (
    value != null &&
    content !== undefined &&
    Object.hasOwn(slot.contentByBranchId, branchId)
  ) {
    return { branchId, content };
  }
  return { branchId: slot.fallbackBranchId, content: slot.fallbackContent };
}

function getDynamicSlotMap(
  ctx: Context,
): Record<string, ResolvedDynamicSlot> | undefined {
  return ctx.get([DYNAMIC_SLOTS_CONTEXT_KEY]) as
    | Record<string, ResolvedDynamicSlot>
    | undefined;
}

/**
 * Renders the winning branch's content (else the fallback) through the same engine
 * + context so nested `{{ vars }}` resolve per recipient. An unknown slot renders
 * empty; the `parse` hook rejects missing/unquoted ids so the tag matches only what
 * the prefetch regex loads.
 */
export function registerDynamicSlotTag(engine: Liquid): void {
  engine.registerTag(DYNAMIC_SLOT_TAG, {
    parse(token) {
      if (!/(?:^|\s)id\s*:\s*["']/.test(token.args)) {
        throw new Error(
          `${DYNAMIC_SLOT_TAG} tag requires a quoted id. Use {% ${DYNAMIC_SLOT_TAG} id:"<usage_id>" %}.`,
        );
      }
    },
    render(ctx, emitter, hash) {
      const rawId = (hash as unknown as Record<string, unknown>).id;
      if (typeof rawId !== "string") return;

      const slot = getDynamicSlotMap(ctx)?.[rawId];
      if (slot === undefined) return;

      const { content } = servedBranch(slot, ctx.get([slot.column]));
      emitter.write(engine.parseAndRenderSync(content, ctx.getAll()));
    },
  });
}
